<template>
  <DsButton
    @click="handleToggle"
    :disabled="isLoading"
    :title="isBookmarked ? 'Remove bookmark' : 'Add bookmark'"
    :class="buttonClass"
    :variant="variant"
    size="small"
    :icon="isBookmarked ? 'bookmark-filled' : 'bookmark'"
    :label="showLabel ? (isBookmarked ? 'Bookmarked' : 'Bookmark') : ''"
  >
  </DsButton>
</template>

<script>
  import { toggleBookmark } from '@/api/knowledge-base'
  import Icon from '@/components/Icon/Icon.vue'

  export default {
    name: 'BookmarkButton',
    components: {
      Icon
    },
    props: {
      postId: {
        type: [String, Number],
        required: true
      },
      isBookmarked: {
        type: Boolean,
        default: false
      },
      bookmarksCount: {
        type: Number,
        default: 0
      },
      communityId: {
        type: [String, Number],
        default: null
      },
      showLabel: {
        type: Boolean,
        default: true
      },
      showCount: {
        type: Boolean,
        default: false
      },
      variant: {
        type: String,
        default: 'default', // 'default', 'minimal'
        validator: (value) => ['default', 'rounded-secondary'].includes(value)
      },
    },
    data () {
      return {
        isLoading: false,
        localBookmarked: this.isBookmarked,
        localCount: this.bookmarksCount
      }
    },
    computed: {
      buttonClass () {
        if (this.variant === 'minimal') {
          return this.localBookmarked
            ? 'text-primary bg-primary/10 border-primary/20 hover:bg-primary/20'
            : 'text-gray-500 bg-white border-gray-300 hover:text-primary hover:border-primary/20 hover:bg-primary/10'
        }

        // Default variant
        return this.localBookmarked
          ? 'text-primary bg-primary/10 border-primary/20 hover:bg-primary/20'
          : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 hover:text-primary'
      },
      iconClass () {
        return this.localBookmarked ? 'text-primary' : 'text-gray-400'
      },
      isMinimal () {
        return this.variant === 'minimal'
      }
    },
    watch: {
      isBookmarked (newValue) {
        this.localBookmarked = newValue
      },
      bookmarksCount (newValue) {
        this.localCount = newValue
      }
    },
    methods: {
      async handleToggle () {
        if (this.isLoading) return

        this.isLoading = true

        try {
          const response = await toggleBookmark(this.postId, this.communityId)

          if (response.success) {
            // Update local state
            this.localBookmarked = response.is_bookmarked
            this.localCount = response.bookmarks_count

            // Emit event for parent components to update their state
            this.$emit('bookmarkToggled', {
              postId: this.postId,
              isBookmarked: response.is_bookmarked,
              bookmarksCount: response.bookmarks_count
            })
          }
        } catch (error) {
          console.error('Error toggling bookmark:', error)
        } finally {
          this.isLoading = false
        }
      }
    }
  }
</script>

<style scoped>
  /* Loading state */
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* Focus states */
  button:focus {
    box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.1);
  }
</style>
